/**
 * Article Detail Page
 * NIP-23 Long-form content reader
 */

import { useState, useEffect } from 'react'
import { useParams, Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { ArrowLeft, Calendar, Clock, Share2, Bookmark } from 'lucide-react'
import { Event, nip19 } from 'nostr-tools'
import { format } from 'date-fns'
import { tr } from 'date-fns/locale'
import { getNostrPool } from '../lib/nostrPool'
import { RELAYS } from '../config/constants'
import { useNostrProfile } from '../hooks/useNostrProfile'
import { getAvatarUrl } from '../lib/avatarUtils'
import { useSingleBookmark } from '../hooks/useBookmarks'
import DOMPurify from 'dompurify'
import { generateTopicUrl } from '../lib/urlUtils'

export function ArticlePage() {
  const { identifier } = useParams<{ identifier: string }>()
  const [article, setArticle] = useState<Event | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Parse identifier - can be note1... or naddr1...
  useEffect(() => {
    if (!identifier) return

    const fetchArticle = async () => {
      setIsLoading(true)
      setError(null)

      try {
        const pool = getNostrPool()
        let filter: any

        // Try to decode as naddr
        if (identifier.startsWith('naddr')) {
          const decoded = nip19.decode(identifier)
          if (decoded.type === 'naddr') {
            const data = decoded.data as { kind: number; pubkey: string; identifier: string }
            filter = {
              kinds: [data.kind],
              authors: [data.pubkey],
              '#d': [data.identifier],
              limit: 1
            }
          }
        } else if (identifier.startsWith('note')) {
          const decoded = nip19.decode(identifier)
          if (decoded.type === 'note') {
            filter = {
              ids: [decoded.data as string],
              limit: 1
            }
          }
        } else {
          // Assume it's a raw event ID or d-tag identifier
          filter = {
            kinds: [30023],
            '#d': [identifier],
            limit: 1
          }
        }

        const events = await pool.querySync(RELAYS, filter)

        if (events.length > 0) {
          setArticle(events[0])
        } else {
          setError('Makale bulunamadı')
        }
      } catch (err) {
        console.error('Article fetch error:', err)
        setError('Makale yüklenirken hata oluştu')
      } finally {
        setIsLoading(false)
      }
    }

    fetchArticle()
  }, [identifier])

  // Extract article metadata from tags
  const getTitle = () => article?.tags.find(t => t[0] === 'title')?.[1] || 'Başlıksız'
  const getSummary = () => article?.tags.find(t => t[0] === 'summary')?.[1]
  const getImage = () => article?.tags.find(t => t[0] === 'image')?.[1]
  const getPublishedAt = () => {
    const tag = article?.tags.find(t => t[0] === 'published_at')
    return tag ? new Date(parseInt(tag[1]) * 1000) : new Date((article?.created_at || 0) * 1000)
  }
  const getHashtags = () => article?.tags.filter(t => t[0] === 't').map(t => t[1]) || []

  const { metadata } = useNostrProfile(article?.pubkey || null)
  const { isBookmarked, toggleBookmark } = useSingleBookmark(article?.id || '')

  const authorName = metadata?.display_name || metadata?.name || article?.pubkey?.slice(0, 8) + '...'
  const authorAvatar = metadata?.picture || getAvatarUrl(authorName)

  // Estimate reading time
  const getReadingTime = () => {
    if (!article) return 0
    const wordCount = article.content.split(/\s+/).length
    return Math.ceil(wordCount / 200) // ~200 words per minute
  }

  // Render markdown-ish content
  const renderContent = () => {
    if (!article) return ''

    let content = article.content

    // Basic markdown rendering
    // Headers
    content = content.replace(/^### (.+)$/gm, '<h3 class="text-xl font-bold mt-6 mb-3 text-gray-900 dark:text-white">$1</h3>')
    content = content.replace(/^## (.+)$/gm, '<h2 class="text-2xl font-bold mt-8 mb-4 text-gray-900 dark:text-white">$1</h2>')
    content = content.replace(/^# (.+)$/gm, '<h1 class="text-3xl font-bold mt-8 mb-4 text-gray-900 dark:text-white">$1</h1>')

    // Bold and italic
    content = content.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    content = content.replace(/\*(.+?)\*/g, '<em>$1</em>')

    // Links
    content = content.replace(/\[(.+?)\]\((.+?)\)/g, '<a href="$2" target="_blank" rel="noopener" class="text-mavi-600 hover:underline">$1</a>')

    // Paragraphs
    content = content.split('\n\n').map(p =>
      p.startsWith('<h') ? p : `<p class="mb-4 leading-relaxed">${p}</p>`
    ).join('')

    return DOMPurify.sanitize(content, {
      ALLOWED_TAGS: ['h1', 'h2', 'h3', 'p', 'strong', 'em', 'a', 'br', 'ul', 'ol', 'li', 'blockquote', 'code', 'pre'],
      ALLOWED_ATTR: ['href', 'target', 'rel', 'class']
    })
  }

  const handleShare = () => {
    if (navigator.share && article) {
      navigator.share({
        title: getTitle(),
        text: getSummary(),
        url: window.location.href
      })
    } else {
      navigator.clipboard.writeText(window.location.href)
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="w-8 h-8 border-4 border-mavi-500 border-t-transparent rounded-full animate-spin" />
      </div>
    )
  }

  if (error || !article) {
    return (
      <div className="text-center py-16">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
          {error || 'Makale bulunamadı'}
        </h2>
        <Link to="/flow/mavililer" className="text-mavi-600 hover:underline">
          Akışa dön
        </Link>
      </div>
    )
  }

  return (
    <motion.article
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="max-w-3xl mx-auto"
    >
      {/* Back button */}
      <Link
        to="/flow/mavililer"
        className="inline-flex items-center gap-2 text-gray-500 hover:text-mavi-600 mb-6 transition-colors"
      >
        <ArrowLeft className="w-4 h-4" />
        akışa dön
      </Link>

      {/* Cover image */}
      {getImage() && (
        <div className="relative aspect-video rounded-2xl overflow-hidden mb-8">
          <img
            src={getImage()}
            alt={getTitle()}
            className="w-full h-full object-cover"
          />
        </div>
      )}

      {/* Header */}
      <header className="mb-8">
        <h1 className="text-3xl md:text-4xl font-poppins font-bold text-gray-900 dark:text-white mb-4">
          {getTitle()}
        </h1>

        {getSummary() && (
          <p className="text-lg text-gray-600 dark:text-gray-400 font-merriweather mb-6">
            {getSummary()}
          </p>
        )}

        {/* Author and meta */}
        <div className="flex flex-wrap items-center gap-4 pb-6 border-b border-surface-200 dark:border-surface-700">
          <Link
            to={`/profile/${article.pubkey}`}
            className="flex items-center gap-3"
          >
            <img
              src={authorAvatar}
              alt={authorName}
              className="w-12 h-12 rounded-full object-cover border-2 border-surface-200 dark:border-surface-700"
            />
            <div>
              <p className="font-poppins font-medium text-gray-900 dark:text-white">
                {authorName}
              </p>
              <div className="flex items-center gap-3 text-sm text-gray-500">
                <span className="flex items-center gap-1">
                  <Calendar className="w-3.5 h-3.5" />
                  {format(getPublishedAt(), 'd MMMM yyyy', { locale: tr })}
                </span>
                <span className="flex items-center gap-1">
                  <Clock className="w-3.5 h-3.5" />
                  {getReadingTime()} dk okuma
                </span>
              </div>
            </div>
          </Link>

          <div className="flex items-center gap-2 ml-auto">
            <button
              onClick={toggleBookmark}
              className={`p-2 rounded-lg transition-colors ${isBookmarked
                ? 'bg-mavi-100 dark:bg-mavi-900/30 text-mavi-600'
                : 'hover:bg-surface-100 dark:hover:bg-surface-800 text-gray-500'
                }`}
              title={isBookmarked ? 'Kayıtlı' : 'Kaydet'}
            >
              <Bookmark className={`w-5 h-5 ${isBookmarked ? 'fill-mavi-600' : ''}`} />
            </button>
            <button
              onClick={handleShare}
              className="p-2 rounded-lg hover:bg-surface-100 dark:hover:bg-surface-800 text-gray-500 transition-colors"
              title="Paylaş"
            >
              <Share2 className="w-5 h-5" />
            </button>
          </div>
        </div>
      </header>

      {/* Content */}
      <div
        className="prose prose-lg max-w-none dark:prose-invert font-merriweather"
        dangerouslySetInnerHTML={{ __html: renderContent() }}
      />

      {/* Tags */}
      {getHashtags().length > 0 && (
        <div className="flex flex-wrap gap-2 mt-8 pt-8 border-t border-surface-200 dark:border-surface-700">
          {getHashtags().map(tag => (
            <Link
              key={tag}
              to={generateTopicUrl(tag, tag)}
              className="px-3 py-1 bg-surface-100 dark:bg-surface-800 text-gray-600 dark:text-gray-400 rounded-full text-sm font-poppins hover:bg-mavi-100 dark:hover:bg-mavi-900/30 hover:text-mavi-600 transition-colors"
            >
              #{tag}
            </Link>
          ))}
        </div>
      )}

      {/* Author card */}
      <div className="mt-8 p-6 bg-surface-50 dark:bg-surface-800/50 rounded-2xl">
        <div className="flex items-start gap-4">
          <Link to={`/profile/${article.pubkey}`}>
            <img
              src={authorAvatar}
              alt={authorName}
              className="w-16 h-16 rounded-full object-cover border-2 border-surface-200 dark:border-surface-700"
            />
          </Link>
          <div className="flex-1">
            <Link
              to={`/profile/${article.pubkey}`}
              className="font-poppins font-bold text-lg text-gray-900 dark:text-white hover:text-mavi-600 transition-colors"
            >
              {authorName}
            </Link>
            {metadata?.about && (
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1 line-clamp-2">
                {metadata.about}
              </p>
            )}
          </div>
        </div>
      </div>
    </motion.article>
  )
}
